import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Bot,
  CalendarDays,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  FileText,
  FolderKanban,
  Inbox,
  PenLine,
  Repeat2,
  Route,
  Sparkles,
  X,
} from "lucide-react";
import { useAuth } from "@/state/AuthProvider";
import { useAppState } from "@/state/AppStateProvider";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/shell/PageHeader";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { Stamp } from "@/components/badges/Stamp";
import { toast } from "sonner";
import { buildDashboardWorkspace, getDashboardActions } from "@/lib/dashboardWorkspace";
import { buildOnboardingChecklist } from "@/lib/onboardingChecklist";
import { cn, timeAgo, formatDateTime } from "@/lib/utils";

const ONBOARDING_DISMISS_KEY = "sce_onboarding_dismissed_v1";

const fade = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.28 } };

const ACTION_ICONS = {
  ai_post: Sparkles,
  manual_post: PenLine,
  repurpose: Repeat2,
  write_blog: FileText,
  import: Inbox,
  calendar: CalendarDays,
};

const FOCUS_ICONS = {
  run: Bot,
  review: ClipboardCheck,
  inbox: Inbox,
  project: FolderKanban,
  calendar: CalendarDays,
  create: Sparkles,
};

const KIND_LABELS = {
  run: "Agent",
  review: "Review",
  inbox: "Inbox",
  project: "Project",
  calendar: "Schedule",
  create: "Create",
};

export default function Dashboard() {
  const { user } = useAuth();
  const { state } = useAppState();
  const workspace = useMemo(() => buildDashboardWorkspace(state), [state]);
  const actions = useMemo(() => getDashboardActions(), []);
  const firstName = user?.name || "Adam";

  return (
    <motion.div {...fade} className="space-y-6 pb-20" data-testid="dashboard-page">
      <PageHeader
        eyebrow="Dashboard"
        title={`${firstName}, run the content day from here.`}
        description="Start a post, clear the review queue, and keep everything moving — without turning the dashboard into another editor."
        testId="dashboard-page-header"
        actions={(
          <>
            <Button asChild className="h-10 gap-1.5 rounded-md" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }} data-testid="dashboard-start-create">
              <Link to="/create"><Sparkles className="h-4 w-4" /> Create AI post</Link>
            </Button>
            {workspace.primaryFocus?.to ? (
              <Button asChild variant="outline" className="h-10 gap-1.5 rounded-md border-[color:var(--border-subtle)] bg-card text-[color:var(--text-2)] hover:bg-[color:var(--surface-2)]" data-testid="dashboard-next-move">
                <Link to={workspace.primaryFocus.to}><Route className="h-4 w-4" /> Next move</Link>
              </Button>
            ) : null}
          </>
        )}
      />

      <OnboardingChecklist state={state} />

      <StatChips metrics={workspace.metrics} />

      <AttentionQueuePanel items={workspace.attentionQueue} />

      <QuickStartRow actions={actions} />
    </motion.div>
  );
}

function StatChips({ metrics }) {
  const chips = [
    { id: "review", label: "Needs review", value: metrics.reviewQueue, to: "/library", icon: ClipboardCheck },
    { id: "inbox", label: "Inbox", value: metrics.inboxOpen, to: "/inbox", icon: Inbox },
    { id: "scheduled", label: "Scheduled", value: metrics.scheduled, to: "/calendar", icon: CalendarDays },
    { id: "projects", label: "Projects", value: metrics.projectsActive, to: "/projects", icon: FolderKanban },
  ];

  return (
    <section className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="dashboard-stat-chips">
      {chips.map(({ id, label, value, to, icon: Icon }) => (
        <Link
          key={id}
          to={to}
          data-testid={`dashboard-stat-${id}`}
          className="group flex items-center justify-between gap-3 rounded-xl border bg-card p-4 inner-highlight transition hover:border-[color:var(--accent-blue)]/40 hover:bg-[color:var(--surface-3)]"
          style={{ borderColor: "var(--border-subtle)" }}
        >
          <span className="min-w-0">
            <span className="block text-2xl font-bold tracking-[-0.03em] text-foreground">{value}</span>
            <span className="mt-0.5 block text-[12.5px] font-medium text-muted-foreground">{label}</span>
          </span>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border bg-[color:var(--surface-2)] text-[color:var(--accent-cyan)]" style={{ borderColor: "var(--border-subtle)" }}>
            <Icon className="h-4 w-4" />
          </span>
        </Link>
      ))}
    </section>
  );
}

function AttentionQueuePanel({ items }) {
  return (
    <section data-testid="dashboard-attention-queue">
      <div className="mb-3 flex items-end justify-between gap-3 px-1">
        <div className="min-w-0">
          <h2 className="text-base font-semibold tracking-[-0.01em] text-foreground">Needs you now</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">One prioritized lane across runs, review, inbox, projects, and calendar.</p>
        </div>
        <Link to="/create" className="shrink-0 text-sm font-semibold text-[color:var(--accent-cyan)] hover:underline">Start create</Link>
      </div>
      {items.length > 0 ? (
        <div className="grid gap-3">
          {items.map((item) => (
            <AttentionRow key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <EmptyPanel icon={CheckCircle2} title="You're all clear" description="Nothing needs you right now. Start the next post from Create." />
      )}
    </section>
  );
}

function AttentionRow({ item }) {
  const { approveAsset } = useAppState();
  const Icon = FOCUS_ICONS[item.kind] || Sparkles;
  const overLimit = item.charLimit && item.charCount != null && item.charCount > item.charLimit;
  const dateText = item.at ? (item.atUpcoming ? formatDateTime(item.at) : timeAgo(item.at)) : null;

  const canApprove = item.kind === "review" && item.status === "needs_review" && item.assetId;
  const isDone = ["approved", "scheduled", "published", "done"].includes(item.status);
  const handleApprove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    approveAsset(item.assetId);
    toast.success("Post approved");
  };

  return (
    <Link to={item.to} className="group grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border bg-[color:var(--surface-1)] px-3 py-3 transition hover:border-[color:var(--accent-blue)]/40 hover:bg-[color:var(--surface-3)]" style={{ borderColor: "var(--border-subtle)" }}>
      <span className="flex h-11 w-11 items-center justify-center rounded-md border border-[color:var(--accent-blue)]/30 bg-[color:var(--accent-blue)]/12 text-[color:var(--accent-cyan)]">
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {KIND_LABELS[item.kind] || "Item"}
          </span>
          {item.platform ? <PlatformBadge platform={item.platform} compact /> : null}
          {item.status ? <StatusBadge status={item.status} /> : null}
          {(item.platforms || []).slice(0, 3).map((p) => <PlatformBadge key={p} platform={p} compact />)}
          {item.charLimit ? (
            <span className={cn("text-[11px] font-medium", overLimit ? "text-[color:var(--accent-amber)]" : "text-muted-foreground")}>
              {item.charCount}/{item.charLimit} chars{overLimit ? " · over" : ""}
            </span>
          ) : null}
        </span>
        <span className="mt-1.5 block truncate text-sm font-semibold text-foreground">{item.title}</span>
        <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">{item.description}</span>
        {dateText ? <span className="mt-0.5 block text-[11px] text-muted-foreground">{item.atLabel || "Updated"} {dateText}</span> : null}
      </span>
      {canApprove ? (
        <Stamp label="APPROVE" onClick={handleApprove} title="Approve this post" />
      ) : isDone ? (
        <Stamp label="DONE" done title="Completed" />
      ) : (
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-[color:var(--accent-blue)]" />
      )}
    </Link>
  );
}

function QuickStartRow({ actions }) {
  return (
    <section data-testid="dashboard-actions">
      <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Start something</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        {actions.map((action) => {
          const Icon = ACTION_ICONS[action.id] || Sparkles;
          return (
            <Link
              key={action.id}
              to={action.to}
              data-testid={`dashboard-action-${action.id}`}
              title={action.description}
              className="group flex items-center gap-2.5 rounded-lg border bg-card px-3 py-2.5 inner-highlight transition hover:border-[color:var(--accent-blue)]/40 hover:bg-[color:var(--surface-3)]"
              style={{ borderColor: "var(--border-subtle)" }}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-[color:var(--surface-2)] text-[color:var(--accent-cyan)]" style={{ borderColor: "var(--border-subtle)" }}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground">{action.label}</span>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function OnboardingChecklist({ state }) {
  const checklist = useMemo(() => buildOnboardingChecklist(state), [state]);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(ONBOARDING_DISMISS_KEY) === "1"; } catch (_) { return false; }
  });

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(ONBOARDING_DISMISS_KEY, "1"); } catch (_) { /* ignore */ }
  };

  // Hide once the operator dismisses it or finishes the core steps.
  if (dismissed || checklist.allCoreDone) return null;

  const pct = Math.round((checklist.completedCore / checklist.totalCore) * 100);

  return (
    <section
      className="rounded-xl border bg-card p-4 inner-highlight shadow-[0_6px_16px_rgba(18, 22, 20,0.05)] md:p-5"
      style={{ borderColor: "var(--border-subtle)" }}
      data-testid="dashboard-onboarding"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[color:var(--accent-cyan)]" />
          <div>
            <h2 className="text-sm font-semibold text-1">Get started</h2>
            <p className="text-[11.5px] text-muted-foreground">A few steps to your first published post.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11.5px] font-medium text-muted-foreground" data-testid="dashboard-onboarding-progress">
            {checklist.completedCore} of {checklist.totalCore}
          </span>
          <button type="button" onClick={dismiss} aria-label="Dismiss get started" className="rounded-md p-1 text-muted-foreground hover:bg-white/[0.06]" data-testid="dashboard-onboarding-dismiss">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--surface-3)]">
        <div className="h-full rounded-full bg-[color:var(--accent-cyan)] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>

      <ul className="mt-3 space-y-1.5" data-testid="dashboard-onboarding-steps">
        {checklist.steps.map((step) => (
          <li
            key={step.id}
            className="flex items-center gap-3 rounded-lg border p-2.5"
            style={{ borderColor: "var(--border-subtle)", backgroundColor: step.done ? "rgba(16,185,129,0.06)" : "var(--surface-2)" }}
            data-testid={`dashboard-onboarding-step-${step.id}`}
          >
            {step.done
              ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
              : <Circle className="h-4 w-4 shrink-0 text-slate-300" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className={cn("text-[13px] font-medium", step.done ? "text-muted-foreground line-through" : "text-1")}>{step.label}</span>
                {step.optional && <span className="rounded-full px-1.5 py-0.5 text-[10px] text-muted-foreground" style={{ backgroundColor: "rgba(150, 160, 152,0.16)" }}>optional</span>}
              </div>
              {!step.done && <p className="truncate text-[11.5px] text-muted-foreground">{step.description}</p>}
            </div>
            {!step.done && (
              <Button asChild size="sm" variant="outline" className="h-7 shrink-0 gap-1 text-xs">
                <Link to={step.to} data-testid={`dashboard-onboarding-cta-${step.id}`}>{step.cta} <ArrowRight className="h-3 w-3" /></Link>
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function EmptyPanel({ icon: Icon, title, description }) {
  return (
    <div className="rounded-lg border border-dashed bg-[color:var(--surface-1)] px-6 py-8 text-center" style={{ borderColor: "var(--border-subtle)" }}>
      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg border bg-[color:var(--surface-2)] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-xs leading-[1.45] text-muted-foreground">{description}</p>
    </div>
  );
}
